<template>
  <div class="img-container">
    <div class="header">
      <span class="title">选择策略</span>
      <el-select v-model="strategy" class="strategy-select">
        <el-option
          v-for="item in options"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
    </div>
    <div class="main">
      <div class="main-up">
        <div class="main-up-content">
          <div v-if="!imageUrl" class="upload-placeholder">
            <el-icon :size="100" class="upload-icon"><PictureFilled /></el-icon>
            <p class="upload-tip">
              点击下方【本地上传】后，点击【生成】依据策略生成U盘可用文件
            </p>
          </div>

          <div v-if="imageUrl" class="preview-container">
            <img :src="imageUrl" class="preview-image" />
          </div>

          <div class="progress-container" v-if="percentage">
            <div class="progress-wrapper" v-if="!auditResult && !isGenerating">
              <el-progress
                class="custom-progress"
                :status="status"
                :text-inside="true"
                :stroke-width="20"
                :percentage="percentage"
              >
              </el-progress>
              <div class="status-text">
                {{ uploadMap[status] || "上传中" }}
              </div>
              <div v-if="status === 'exception'" class="error-text">
                上传失败,请重试
              </div>
            </div>

            <div class="audit-result" v-if="auditResult">
              <div class="audit-message">
                {{ auditMessage }}
              </div>
              <div v-if="loadable" class="img-download">
                <div class="download-btn" @click="download">
                  <img
                    class="download-icon"
                    :src="downloadImg"
                    alt="download"
                  />
                </div>
              </div>
            </div>

            <div class="watermark-loading" v-if="isGenerating && !auditResult">
              <el-icon class="loading-icon is-loading"><Loading /></el-icon>
              <div class="loading-text">正在进行无感水印,请稍后...</div>
            </div>
          </div>
        </div>
      </div>

      <div class="main-down">
        <div class="main-down-action">
          <div class="file-upload-container">
            <el-input
              size="large"
              class="upload-input"
              placeholder="请选择图片文件"
              readonly
              :value="backupFile.name || ''"
            >
              <template #prefix>
                <el-icon class="file-icon"><Document /></el-icon>
              </template>
              <template #append>
                <el-upload
                  :show-file-list="false"
                  action="#"
                  :auto-upload="true"
                  :http-request="customUpload"
                  :before-upload="beforeUpload"
                  :disabled="isGenerating || isUploading"
                  accept=".JPG,.JPEG,.PNG,.BMP,.GIF"
                >
                  <template #trigger>
                    <el-button
                      class="upload-btn"
                      type="primary"
                      size="large"
                      :disabled="isGenerating || isUploading"
                    >
                      <el-icon class="upload-icon"><Upload /></el-icon>
                      {{ isUploading ? "上传中..." : "本地上传" }}
                    </el-button>
                  </template>
                </el-upload>
              </template>
            </el-input>
          </div>
          <el-button
            class="audit-btn"
            size="large"
            :disabled="status !== 'success' || isGenerating || isUploading"
            :loading="isGenerating"
            type="primary"
            @click="generate"
          >
            <el-icon class="audit-icon" v-if="!isGenerating"><Check /></el-icon>
            {{ isGenerating ? "处理中..." : "无感水印" }}
          </el-button>
        </div>
        <div class="main-down-text">
          支持 JPG、JPEG、PNG、BMP、GIF 格式的图片
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { ElMessageBox, ElNotification } from "element-plus";
import { Loading } from "@element-plus/icons-vue";
import { uploadImg, generateImg, downloadFile } from "../../api/index";
import downloadImg from "../../assets/downloadImg.png";
import { AuditResultMap, uploadMap } from "../../enum";
import { Document, Upload, Check } from "@element-plus/icons-vue";

const strategy = ref("1");
const options = [
  {
    value: "1",
    label: "默认策略",
  },
];

const imageUrl = ref("");

const beforeUpload = (file: File) => {
  imageUrl.value = URL.createObjectURL(file);
  return true;
};

const backupFile = ref<File>({} as File);
const percentage = ref(0);
const status = ref("");
// 进度条增长
const timer = ref<ReturnType<typeof setInterval> | null>(null);
function increase() {
  timer.value = setInterval(() => {
    percentage.value += 10;
  }, 1000);
}
watch(
  () => percentage.value,
  (percen) => {
    if (percen >= 90 && timer.value) {
      clearInterval(timer.value);
      timer.value = null;
    }
  }
);

const loadable = computed(() => auditResult.value === "2");
const auditResult = ref("");
const auditMessage = ref("");
const generateFilePath = ref("");
const isGenerating = ref(false);
const isUploading = ref(false);

const customUpload = async (file: { file: File }) => {
  backupFile.value = file.file;
  percentage.value = 0;
  status.value = "";
  auditResult.value = "";
  auditMessage.value = "";
  isUploading.value = true;

  increase();
  try {
    const { code, message, content } = await uploadImg(file.file);
    if (code === "success") {
      percentage.value = 100;
      status.value = "success";
      generateFilePath.value = (content as { filename: string }).filename;
    } else {
      if (timer.value) {
        clearInterval(timer.value);
        timer.value = null;
      }
      status.value = "exception";
    }
    ElNotification({
      title: message,
      type: code === "success" ? "success" : "error",
    });
  } catch (error: any) {
    if (timer.value) {
      clearInterval(timer.value);
      timer.value = null;
    }
    status.value = "exception";
    ElNotification({
      title: error?.message || "上传失败",
      type: "error",
    });
  } finally {
    isUploading.value = false;
  }
};

const generate = async () => {
  isGenerating.value = true;
  auditResult.value = "";
  auditMessage.value = "";
  try {
    const { code, content, message } = await generateImg(
      generateFilePath.value
    );
    auditResult.value = (content as { status_code: string }).status_code || "0";
    auditMessage.value = message || AuditResultMap[auditResult.value];
    ElNotification({
      title: message,
      type: code === "success" ? "success" : "error",
    });
  } catch (error: any) {
    ElNotification({
      title: error?.message || "无感水印处理失败",
      type: "error",
    });
  } finally {
    isGenerating.value = false;
  }
};

const download = async () => {
  try {
    const res = await downloadFile(generateFilePath.value);
    if (res?.code === "error") throw new Error(res.message);
    const blob = new Blob([res.content as string], {
      type: (res.content as { type: string }).type,
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = generateFilePath.value;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error("下载失败", error);
  }
};
</script>

<style lang="scss" scoped>
// 直接复用 Img.vue 的样式
.img-container {
  padding: 24px;

  .header {
    display: flex;
    align-items: center;
    margin-bottom: 32px;

    .title {
      font-size: 18px;
      font-weight: 600;
      margin-right: 24px;
      color: #333;
    }

    .strategy-select {
      width: 180px;
    }
  }

  .main {
    background: linear-gradient(145deg, #2c3e50, #3498db);
    min-height: 500px;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    position: relative;

    &-up {
      color: #fff;
      min-height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;
      position: relative;
      overflow: hidden;

      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.05);
        backdrop-filter: blur(10px);
      }

      &-content {
        position: relative;
        text-align: center;
        width: 100%;
        max-width: 600px;

        .preview-container {
          width: 100%;
          border-radius: 12px;
          overflow: hidden;
          max-height: 400px;
          margin-bottom: 10px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);

          .preview-image {
            width: 100%;
            height: auto;
            object-fit: contain;
            background: #000;
          }
        }

        .upload-placeholder {
          padding: 40px;
          border: 2px dashed rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          transition: all 0.3s ease;

          &:hover {
            border-color: rgba(255, 255, 255, 0.5);
          }
        }

        .upload-icon {
          color: #fff;
          margin-bottom: 24px;
          opacity: 0.8;
          filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
        }

        .upload-tip {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.6;
        }

        .progress-container {
          background: rgba(255, 255, 255, 0.1);
          padding: 24px;
          border-radius: 12px;
          backdrop-filter: blur(10px);

          .custom-progress {
            margin-bottom: 16px;
          }

          .status-text {
            font-size: 16px;
            color: rgba(255, 255, 255, 0.9);
          }

          .error-text {
            color: #ff6b6b;
            margin-top: 8px;
            font-size: 14px;
          }
        }

        .audit-result {
          .audit-message {
            font-size: 18px;
            margin-bottom: 14px;
            color: rgba(255, 255, 255, 0.9);
          }
        }
      }
    }

    &-down {
      padding: 24px;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(20px);

      &-action {
        display: flex;
        align-items: center;
        gap: 16px;

        .file-upload-container {
          flex: 1;
          position: relative;

          .file-icon {
            margin-right: 8px;
            font-size: 20px;
            color: rgba(255, 255, 255, 0.7);
          }

          .upload-input {
            :deep(.el-input__wrapper) {
              padding-left: 16px;
              background: rgba(255, 255, 255, 0.1);
              border: 2px solid transparent;
              transition: all 0.3s ease;

              &:hover {
                background: rgba(255, 255, 255, 0.15);
                border-color: rgba(255, 255, 255, 0.1);
              }
            }
          }
        }

        .upload-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #4caf50, #45a049);
          border: none;
          padding: 12px 24px;
          transition: all 0.3s ease;
          font-weight: 600;
          border-radius: 8px;
          color: white;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
          box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);

          .upload-icon {
            font-size: 18px;
          }

          &:hover {
            background: linear-gradient(135deg, #45a049, #388e3c);
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
          }

          &:active {
            transform: translateY(1px);
          }
        }

        .audit-btn {
          min-width: 120px;
          height: 48px;
          font-size: 16px;
          border-radius: 8px;
          transition: all 0.3s ease;
          background: linear-gradient(135deg, #2196f3, #1976d2);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-weight: 600;
          color: white;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
          box-shadow: 0 4px 15px rgba(33, 150, 243, 0.3);

          .audit-icon {
            font-size: 18px;
          }

          &:not(:disabled):hover {
            background: linear-gradient(135deg, #1976d2, #1565c0);
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(33, 150, 243, 0.4);
          }

          &:not(:disabled):active {
            transform: translateY(1px);
          }

          &:disabled {
            background: linear-gradient(135deg, #9e9e9e, #757575);
            color: rgba(255, 255, 255, 0.6);
            box-shadow: none;
            cursor: not-allowed;
          }
        }
      }

      &-text {
        color: rgba(255, 255, 255, 0.7);
        margin-top: 12px;
        font-size: 14px;
      }
    }
  }

  .img-download {
    height: 42px;
    cursor: pointer;
    .download-btn {
      height: 100%;
      .download-icon {
        height: 100%;
      }

      .download-text {
        color: #fff;
        font-size: 16px;
        font-weight: 600;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
      }
    }
  }
}

:deep(.el-input__wrapper) {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  backdrop-filter: blur(10px);
  box-shadow: none;

  &:hover,
  &:focus {
    background: rgba(255, 255, 255, 0.15);
  }

  .el-input__inner {
    color: #fff;
    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }
}

:deep(.el-input-group__append),
:deep(.el-input-group__prepend) {
  background: transparent;
  border: none;
  padding: 0 4px;
}

:deep(.el-progress-bar__outer) {
  background: rgba(255, 255, 255, 0.1);
}

:deep(.el-progress-bar__inner) {
  background: linear-gradient(90deg, #1547ec, #4b7bff);
}

:deep(.el-progress) {
  .el-progress__text {
    color: #fff;
  }
}
</style>
